"use client"

import { CheckCircle2, XCircle, AlertTriangle, Clock, CreditCard, PencilLine } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"

// ─── Types ────────────────────────────────────────────────────────────────────

export type SlipResultVariant =
  | "verified"
  | "duplicate"
  | "not_found"
  | "damaged"
  | "account_mismatch"
  | "no_credits"
  | "invalid_file"
  | "invalid_qr"
  | "timeout"
  | "unsupported"
  | "manual_success"
  | "manual_failed"

export interface SlipResultData {
  mode: "verify" | "manual"
  success: boolean
  errorMessage?: string
  transaction_ref?: string
  sender_name?: string
  sender_account?: string
  receiver_name?: string
  receiver_account?: string
  amount?: number
  transaction_date?: string
  account_label?: string
  bank_code?: string
  is_duplicate?: boolean
  verification_method?: string
  credits_used?: number
  credits_saved?: number
  credits_remaining?: number
  classification_type?: string
  warnings?: Array<{ type: string; severity: string; message: string }>
  branch_name?: string
  matched_transaction_data?: Record<string, unknown> | null
  credit_required?: number
  credit_available?: number
  credit_shortfall?: number
}

// ─── Variant config ───────────────────────────────────────────────────────────

type IconType = "check" | "x" | "warning" | "clock" | "credit" | "pencil"

type VariantConfig = {
  cardBg: string
  iconType: IconType
  iconColorClass: string
  cardTitle: string
  cardTitleColor: string
  showCreditBadges: boolean
  showIncomeBadge: boolean
  isSuccess: boolean
}

const VARIANT_CONFIG: Record<SlipResultVariant, VariantConfig> = {
  verified: {
    cardBg: "bg-green-50",
    iconType: "check",
    iconColorClass: "text-green-600",
    cardTitle: "ตรวจสอบสลิปสำเร็จ",
    cardTitleColor: "text-green-600",
    showCreditBadges: true,
    showIncomeBadge: true,
    isSuccess: true,
  },
  duplicate: {
    cardBg: "bg-orange-50",
    iconType: "warning",
    iconColorClass: "text-orange-500",
    cardTitle: "ซ้ำกับรายการเดิม",
    cardTitleColor: "text-orange-500",
    showCreditBadges: true,
    showIncomeBadge: false,
    isSuccess: true,
  },
  not_found: {
    cardBg: "bg-red-50",
    iconType: "x",
    iconColorClass: "text-red-600",
    cardTitle: "ไม่พบในธนาคาร",
    cardTitleColor: "text-red-600",
    showCreditBadges: true,
    showIncomeBadge: false,
    isSuccess: false,
  },
  damaged: {
    cardBg: "bg-red-50",
    iconType: "x",
    iconColorClass: "text-red-600",
    cardTitle: "สลิปไม่ชัดเจน",
    cardTitleColor: "text-red-600",
    showCreditBadges: true,
    showIncomeBadge: false,
    isSuccess: false,
  },
  account_mismatch: {
    cardBg: "bg-green-50",
    iconType: "warning",
    iconColorClass: "text-orange-500",
    cardTitle: "ผู้รับไม่ตรงกับบัญชีธุรกิจ",
    cardTitleColor: "text-orange-500",
    showCreditBadges: true,
    showIncomeBadge: true,
    isSuccess: true,
  },
  no_credits: {
    cardBg: "bg-red-50",
    iconType: "credit",
    iconColorClass: "text-red-600",
    cardTitle: "เครดิตไม่เพียงพอ",
    cardTitleColor: "text-red-600",
    showCreditBadges: false,
    showIncomeBadge: false,
    isSuccess: false,
  },
  invalid_file: {
    cardBg: "bg-red-50",
    iconType: "x",
    iconColorClass: "text-red-600",
    cardTitle: "ไฟล์ไม่ถูกต้อง",
    cardTitleColor: "text-red-600",
    showCreditBadges: true,
    showIncomeBadge: false,
    isSuccess: false,
  },
  invalid_qr: {
    cardBg: "bg-red-50",
    iconType: "x",
    iconColorClass: "text-red-600",
    cardTitle: "QR Code ไม่ถูกต้อง",
    cardTitleColor: "text-red-600",
    showCreditBadges: true,
    showIncomeBadge: false,
    isSuccess: false,
  },
  timeout: {
    cardBg: "bg-red-50",
    iconType: "clock",
    iconColorClass: "text-red-600",
    cardTitle: "การเชื่อมต่อหมดเวลา",
    cardTitleColor: "text-red-600",
    showCreditBadges: true,
    showIncomeBadge: false,
    isSuccess: false,
  },
  unsupported: {
    cardBg: "bg-red-50",
    iconType: "x",
    iconColorClass: "text-red-600",
    cardTitle: "ธนาคารไม่รองรับ",
    cardTitleColor: "text-red-600",
    showCreditBadges: false,
    showIncomeBadge: false,
    isSuccess: false,
  },
  manual_success: {
    cardBg: "bg-green-50",
    iconType: "pencil",
    iconColorClass: "text-green-600",
    cardTitle: "บันทึกรายการสำเร็จ",
    cardTitleColor: "text-green-600",
    showCreditBadges: false,
    showIncomeBadge: false,
    isSuccess: true,
  },
  manual_failed: {
    cardBg: "bg-red-50",
    iconType: "x",
    iconColorClass: "text-red-600",
    cardTitle: "บันทึกรายการไม่สำเร็จ",
    cardTitleColor: "text-red-600",
    showCreditBadges: false,
    showIncomeBadge: false,
    isSuccess: false,
  },
}

// ─── Icon helper ──────────────────────────────────────────────────────────────

function StatusIcon({ type, colorClass }: { type: IconType; colorClass: string }) {
  const cls = cn("h-12 w-12", colorClass)
  switch (type) {
    case "check":   return <CheckCircle2 className={cls} />
    case "x":       return <XCircle className={cls} />
    case "warning": return <AlertTriangle className={cls} />
    case "clock":   return <Clock className={cls} />
    case "credit":  return <CreditCard className={cls} />
    case "pencil":  return <PencilLine className={cls} />
  }
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface SlipResultDialogProps {
  variant: SlipResultVariant
  data?: SlipResultData
  onClose: () => void
  onRetry?: () => void
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SlipResultDialog({ variant, data, onClose, onRetry }: SlipResultDialogProps) {
  const config = VARIANT_CONFIG[variant]
  const isDuplicate = variant === "duplicate"
  const isNoCredits = variant === "no_credits"
  const isAccountMismatch = variant === "account_mismatch"
  const isManual = variant === "manual_success" || variant === "manual_failed"

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between p-6 pb-4">
        <h2 className="text-xl font-semibold text-gray-900" aria-hidden="true">
          ส่งสลิปตรวจสอบ
        </h2>
      </div>

      {/* Result Card */}
      <div className="px-6 pb-6 max-h-[60vh] overflow-y-auto">
        <div className={cn("rounded-xl p-6 text-center", config.cardBg)}>
          {/* Icon */}
          <div className="flex justify-center mb-3">
            <StatusIcon type={config.iconType} colorClass={config.iconColorClass} />
          </div>

          {/* Card Title */}
          <p className={cn("text-lg font-bold mb-1", config.cardTitleColor)}>
            {config.cardTitle}
          </p>

          {/* Duplicate: no-extra-credit badge */}
          {isDuplicate && (
            <span className="inline-block text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium mb-3">
              ไม่หักเครดิตเพิ่มเติม
            </span>
          )}

          {/* ─── No-credits detail ─────────────────────────────────────── */}
          {isNoCredits && data && (
            <div className="text-left space-y-2 my-4">
              <p className="text-sm text-amber-700 text-center font-medium mb-3">
                Credits ไม่เพียงพอสำหรับการตรวจสอบสลิปนี้
              </p>
              {data.credit_available !== undefined && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Credits คงเหลือ</span>
                  <span className="font-semibold text-amber-700">
                    {data.credit_available.toLocaleString()} Credits
                  </span>
                </div>
              )}
              {data.credit_required !== undefined && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">ต้องการ</span>
                  <span className="font-semibold text-gray-900">
                    {data.credit_required.toLocaleString()} Credits
                  </span>
                </div>
              )}
              {data.credit_shortfall !== undefined && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">ขาด</span>
                  <span className="font-bold text-red-600">
                    {data.credit_shortfall.toLocaleString()} Credits
                  </span>
                </div>
              )}
              <a
                href="/dashboard/billing"
                className="flex items-center justify-center gap-2 mt-3 w-full px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <CreditCard className="h-4 w-4" />
                เติม Credits
              </a>
            </div>
          )}

          {/* ─── Generic detail rows ────────────────────────────────────── */}
          {!isNoCredits && data && (
            <div className="text-left space-y-2 my-4">
              {data.transaction_ref && (
                <div className="flex justify-between text-sm gap-2">
                  <span className="text-gray-500 shrink-0">รหัสอ้างอิง</span>
                  <span className="font-mono text-gray-900 text-xs text-right break-all max-w-[60%]">
                    {data.transaction_ref}
                  </span>
                </div>
              )}
              {data.amount !== undefined && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 shrink-0">ยอดเงิน</span>
                  <span className={cn("font-bold", config.cardTitleColor)}>
                    {data.amount.toLocaleString("th-TH", { minimumFractionDigits: 2 })} บาท
                  </span>
                </div>
              )}
              {data.transaction_date && (
                <div className="flex justify-between text-sm gap-2">
                  <span className="text-gray-500 shrink-0">วันเวลาโอน</span>
                  <span className="text-gray-900 text-right text-xs">
                    {new Date(data.transaction_date).toLocaleString("th-TH", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </span>
                </div>
              )}
              {(data.sender_name || data.sender_account) && (
                <div className="flex justify-between text-sm gap-2">
                  <span className="text-gray-500 shrink-0">ผู้โอน</span>
                  <span className="text-gray-900 text-right">
                    {data.sender_name}
                    {data.sender_account && (
                      <span className="block text-xs text-gray-400">{data.sender_account}</span>
                    )}
                  </span>
                </div>
              )}
              {(data.receiver_name || data.receiver_account) && (
                <div className="flex justify-between text-sm gap-2">
                  <span className="text-gray-500 shrink-0">ผู้รับ</span>
                  <span className="text-gray-900 text-right">
                    {data.receiver_name}
                    {data.receiver_account && (
                      <span className="block text-xs text-gray-400">{data.receiver_account}</span>
                    )}
                  </span>
                </div>
              )}
              {!isManual && data.bank_code && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 shrink-0">ธนาคาร</span>
                  <span className="text-gray-900 font-medium">{data.bank_code}</span>
                </div>
              )}
              {!isManual && data.verification_method && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 shrink-0">วิธีตรวจสอบ</span>
                  <span className="text-gray-900 text-xs">{data.verification_method}</span>
                </div>
              )}
              {data.branch_name && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 shrink-0">สาขา</span>
                  <span className="text-gray-900">{data.branch_name}</span>
                </div>
              )}
              {variant === "manual_success" && data.account_label && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 shrink-0">บัญชีรับ</span>
                  <span className="text-gray-900">{data.account_label}</span>
                </div>
              )}
              {/* Error message for failed cases */}
              {!config.isSuccess && data.errorMessage && (
                <div className="flex justify-between text-sm gap-2">
                  <span className="text-gray-500 shrink-0">รายละเอียด</span>
                  <span className="text-gray-700 text-xs text-right max-w-[65%]">
                    {data.errorMessage}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ─── Credit badges ──────────────────────────────────────────── */}
          {config.showCreditBadges && data && (
            <div className="flex flex-wrap gap-2 justify-center mb-4">
              {isDuplicate ? (
                <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100">
                  ประหยัด {data.credits_saved ?? 1} เครดิต
                </Badge>
              ) : (
                <Badge className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100">
                  ใช้ {data.credits_used ?? 0} เครดิต
                </Badge>
              )}
              {data.credits_remaining !== undefined && (
                <Badge className="bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-100">
                  คงเหลือ {data.credits_remaining} เครดิต
                </Badge>
              )}
              {config.showIncomeBadge && data.classification_type && (
                <Badge
                  className={cn(
                    "border hover:opacity-100",
                    data.classification_type?.toUpperCase() === "EXPENSE"
                      ? "bg-red-100 text-red-700 border-red-200 hover:bg-red-100"
                      : "bg-green-100 text-green-700 border-green-200 hover:bg-green-100"
                  )}
                >
                  {data.classification_type?.toUpperCase() === "EXPENSE" ? "รายจ่าย" : "รายรับ"}
                </Badge>
              )}
            </div>
          )}

          {/* ─── Account-mismatch warning banner ────────────────────────── */}
          {isAccountMismatch && data?.warnings && data.warnings.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-3 text-left">
              {data.warnings
                .filter(
                  (w) =>
                    w.type === "EXTERNAL_ACCOUNT" ||
                    w.type === "ACCOUNT_MISMATCH" ||
                    w.type === "ACCOUNT_NAME_MISMATCH"
                )
                .map((w, i) => (
                  <p key={i} className="text-sm text-orange-700">
                    {w.message}
                  </p>
                ))}
            </div>
          )}

          {/* ─── Generic warnings (non-account-mismatch) ────────────────── */}
          {!isAccountMismatch && data?.warnings && data.warnings.length > 0 && (
            <div className="space-y-1.5">
              {data.warnings.map((w, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex gap-2 p-2 rounded text-xs text-left",
                    w.severity === "high" || w.severity === "critical"
                      ? "bg-red-100 text-red-700"
                      : w.severity === "medium"
                      ? "bg-orange-100 text-orange-700"
                      : "bg-yellow-50 text-yellow-700"
                  )}
                >
                  <span className="font-semibold shrink-0">[{w.severity.toUpperCase()}]</span>
                  <span>{w.message}</span>
                </div>
              ))}
            </div>
          )}

          {/* ─── Duplicate: original transaction panel ───────────────────── */}
          {isDuplicate &&
            data?.matched_transaction_data &&
            (() => {
              const orig = data.matched_transaction_data!
              const origAmount =
                typeof orig.amount === "number" ? orig.amount : Number(orig.amount)
              return (
                <div className="mt-4 border border-orange-200 rounded-lg overflow-hidden text-left">
                  <div className="bg-orange-100 px-3 py-2">
                    <p className="text-xs font-semibold text-orange-700">รายการเดิมที่พบซ้ำ</p>
                  </div>
                  <dl className="divide-y divide-orange-100">
                    {!!orig.transaction_ref && (
                      <div className="flex justify-between gap-2 px-3 py-1.5">
                        <dt className="text-xs text-gray-500 shrink-0">เลขอ้างอิงเดิม</dt>
                        <dd className="font-mono text-xs text-gray-800 text-right break-all">
                          {String(orig.transaction_ref)}
                        </dd>
                      </div>
                    )}
                    {!isNaN(origAmount) && (
                      <div className="flex justify-between gap-2 px-3 py-1.5">
                        <dt className="text-xs text-gray-500 shrink-0">ยอดเงิน</dt>
                        <dd className="text-xs font-bold text-orange-700 text-right">
                          {origAmount.toLocaleString("th-TH", { minimumFractionDigits: 2 })} บาท
                        </dd>
                      </div>
                    )}
                    {!!orig.sender_account_name && (
                      <div className="flex justify-between gap-2 px-3 py-1.5">
                        <dt className="text-xs text-gray-500 shrink-0">ชื่อผู้โอน</dt>
                        <dd className="text-xs text-gray-800 text-right">
                          {String(orig.sender_account_name)}
                        </dd>
                      </div>
                    )}
                    {!!orig.receiver_account_name && (
                      <div className="flex justify-between gap-2 px-3 py-1.5">
                        <dt className="text-xs text-gray-500 shrink-0">ชื่อผู้รับ</dt>
                        <dd className="text-xs text-gray-800 text-right">
                          {String(orig.receiver_account_name)}
                        </dd>
                      </div>
                    )}
                    {!!orig.transaction_date && (
                      <div className="flex justify-between gap-2 px-3 py-1.5">
                        <dt className="text-xs text-gray-500 shrink-0">วันเวลาโอน</dt>
                        <dd className="text-xs text-gray-800 text-right">
                          {new Date(String(orig.transaction_date)).toLocaleString("th-TH", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </dd>
                      </div>
                    )}
                    {!!orig.created_at && (
                      <div className="flex justify-between gap-2 px-3 py-1.5">
                        <dt className="text-xs text-gray-500 shrink-0">บันทึกเมื่อ</dt>
                        <dd className="text-xs text-gray-800 text-right">
                          {new Date(String(orig.created_at)).toLocaleString("th-TH", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              )
            })()}
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 px-6 py-4 border-t">
        {!config.isSuccess && !isNoCredits && onRetry && (
          <Button variant="outline" onClick={onRetry}>
            ลองใหม่
          </Button>
        )}
        <Button
          onClick={onClose}
          className={cn(
            "px-8 text-white",
            config.isSuccess || isDuplicate
              ? "bg-green-500 hover:bg-green-600"
              : "bg-gray-500 hover:bg-gray-600"
          )}
        >
          ปิด
        </Button>
      </div>
    </>
  )
}

// ─── Mapping helper (exported for use in add-slip-dialog) ─────────────────────

export function mapResultToVariant(result: SlipResultData): SlipResultVariant {
  if (result.mode === "manual") {
    return result.success ? "manual_success" : "manual_failed"
  }

  if (result.success) {
    // Duplicate must be checked BEFORE account warnings — a duplicate slip
    // may also have account warnings from earlier validation, but the primary
    // result is always "duplicate" when is_duplicate = true.
    if (result.is_duplicate) return "duplicate"
    const hasAccountWarning = result.warnings?.some(
      (w) =>
        w.type === "EXTERNAL_ACCOUNT" ||
        w.type === "ACCOUNT_MISMATCH" ||
        w.type === "ACCOUNT_NAME_MISMATCH"
    )
    if (hasAccountWarning) return "account_mismatch"
    return "verified"
  }

  if (result.errorMessage === "INSUFFICIENT_CREDITS") return "no_credits"

  const msg = (result.errorMessage ?? "").toLowerCase()
  if (msg.includes("timeout") || msg.includes("หมดเวลา")) return "timeout"
  if (msg.includes("unsupported") || msg.includes("ไม่รองรับ")) return "unsupported"
  if (msg.includes("invalid qr") || msg.includes("qr code")) return "invalid_qr"
  if (msg.includes("invalid file") || msg.includes("ไฟล์ไม่ถูก")) return "invalid_file"
  if (msg.includes("damaged") || msg.includes("quality") || msg.includes("ไม่ชัด")) return "damaged"

  return "not_found"
}
